<template>
    <base-dialog :value="dialog" :width="520" :title="`${$t('common.edit')} ${$t('common.config')}`"
        @input="onInput" @close="hide">
        <template v-if="exercise">
            <edit-sets v-if="!isTabata && exercise.config" dialog-mode class="ma-0"
                :assigned-routine-id="exercise.assignedRoutineId" :exercise-id="exercise.exerciseId"
                :config="exercise.config" :block="exercise.block" :is-circuit="exercise.blockData.isCircuit"
                :routine="exercise.routine" :blocks="exercise.routine.blocks" :category="exercise.category"
                @updatedSet="onEditSets" />

            <hr v-if="!isTabata && exercise.config" class="config-divider" />

            <kimsa-textarea-field :label="$t('common.message_to_coach')"
                :placeholder="$t('common.send_placeholder')" :value="commentaryFromStudent"
                @change="commentaryFromStudent = $event" rows="3" />
            <div class="config-msg-footer">
                <v-btn small depressed color="txtPrimary" class="bg--text normal-btn" :loading="loadingCommentary"
                    :disabled="!canSendToCoach" @click="sendToCoach">
                    {{ $t('common.send') }}
                </v-btn>
            </div>

            <div v-if="commentary" class="coach-comment">
                <b>{{ $t('exercises.comment_of_coach') }}:</b> {{ commentary }}
            </div>
        </template>
    </base-dialog>
</template>

<script>

import { FieldValue } from '../../../utils/Firebase';
import { buildSetsChange } from '@/utils/Routine';
import { clearBlocksAssignedRoutine } from '@/utils/Exercises';
import { sendMessage } from '@/utils/Messages';
import BaseDialog from '../BaseDialog.vue';
import EditSets from '../../student/EditSets.vue';
import KimsaTextareaField from '../inputs/KimsaTextareaField.vue';

export default {
    props: {
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
        commentary: {
            type: String,
            default: null,
        },
    },
    components: {
        BaseDialog,
        EditSets,
        KimsaTextareaField,
    },
    data() {
        return {
            dialog: false,
            exercise: null,
            loadingCommentary: false,
            commentaryFromStudent: '',
        }
    },
    computed: {
        canSendToCoach(vm = this) {
            return !!vm.commentaryFromStudent?.trim()
        },
        coachId(vm = this) {
            return vm.exercise?.routine?.coachId || null
        },
        studentId(vm = this) {
            return vm.exercise?.routine?.studentId || null
        },
        isTabata(vm = this) {
            return vm.exercise?.blockData?.type === 'tabata'
        },
    },
    methods: {
        show(exercise) {
            const vm = this
            vm.exercise = exercise
            vm.commentaryFromStudent = exercise?.commentaryFromStudent || ''
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.$emit('hide')
            vm.$nextTick(() => {
                vm.exercise = false
            })
        },
        onInput(val) {
            if (!val) this.hide()
        },
        async onEditSets(config) {
            const vm = this;

            const change = buildSetsChange({
                block: vm.exercise.block,
                exercise: vm.exercise.name,
                exerciseId: vm.exercise.objectID || vm.exercise.exerciseId || null,
                exerciseIndex: vm.exercise.exerciseIndex,
                previousConfig: vm.exercise.config,
                config,
                t: vm.$t.bind(vm),
            })

            if (change) {
                const saved = await vm.saveAssignedRoutine(vm.exercise.assignedRoutineId, {
                    'advance.changes': FieldValue.arrayUnion(change)
                })
                if (!saved) return vm.hide()

                vm.$emit('updatedSet', {
                    block: vm.exercise.block,
                    exerciseIndex: vm.exercise.exerciseIndex,
                    config: config,
                })
            }
            vm.hide()
        },
        async sendToCoach() {
            const vm = this
            if (!vm.canSendToCoach) return
            const exercise = vm.exercise
            const comment = vm.commentaryFromStudent
            const { coachId, studentId } = vm
            await vm.withLoading(async () => {
                let blocks = clearBlocksAssignedRoutine(exercise.routine.blocks, false)
                blocks[exercise.block - 1].exercises[exercise.exerciseIndex].commentaryFromStudent = comment
                if (!await vm.saveAssignedRoutine(exercise.assignedRoutineId, { blocks: blocks })) return vm.hide()
                await vm.sendMessageToCoach({ exercise, comment, coachId, studentId })
                vm.toast(vm.$t('exercises.commentary_from_student_sent'))

                vm.$emit('sentCommentary', {
                    comment,
                    blockIndex: exercise.block - 1,
                    exerciseIndex: exercise.exerciseIndex
                })

                vm.hide()
            }, 'loadingCommentary')
        },
        async sendMessageToCoach({ exercise, comment, coachId, studentId }) {
            const vm = this

            let message = `${vm.$t('common.date')}: ${vm.$moment().format('DD-MM-YYYY')} \n${vm.$tc('exercises.exercises', 1)}: ${exercise.name} \n${vm.$t('common.comment')}: ${comment}`

            await sendMessage({ message, from: studentId }, coachId, studentId)
        },
    },
}

</script>

<style scoped>
.config-divider {
    border: none;
    border-top: 1px solid var(--v-outline-base);
    margin: 20px 0;
}

.config-msg-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
}

.coach-comment {
    margin-top: 16px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--v-txtSecondary-base);
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-left: 3px solid var(--v-accent-base);
    border-radius: 0 8px 8px 0;
    padding: 10px 12px;
}

.coach-comment b {
    color: var(--v-txtPrimary-base);
}
</style>
